'use client'

import type { VideoGalleryFieldsFragment } from '@/graphql/types'
import { Container, DynamicVideoGallery } from '@ccpgames/eve-web-core'
import type { DynamicVideoGalleryProps, VideoGalleryItemType } from '@ccpgames/eve-web-core'
import { useDlpTheme } from '@/components/providers/DlpThemeProvider'
import { useContentfulEntryLivePreview } from '@/components/providers/useContentfulEntryLivePreview'
import { buildContentfulInspectorProps } from '@/components/providers/contentfulInspectorProps'
import { toLibraryDlpTheme } from '@/lib/dlp-theme'
import { NextImageWrapper } from '@/components/shared/NextImageWrapper'

export function VideoGallery({ entry }: { entry: VideoGalleryFieldsFragment }) {
  const { updatedEntry, inspectorProps } = useContentfulEntryLivePreview(entry)
  const pageTheme = useDlpTheme()
  const theme = toLibraryDlpTheme(pageTheme)
  const embedParentDomains = typeof window === 'undefined' ? [] : [window.location.hostname]

  const view: DynamicVideoGalleryProps['view'] = updatedEntry.view?.toLowerCase() === 'slides' ? 'slides' : 'grid'
  const imageSizes = view === 'slides' ? '70vw' : '(min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw'

  const allowedTypes: ReadonlyArray<VideoGalleryItemType> = ['youtube', 'twitch', 'cdn-video', 'spotify-video'] as const
  const normalizeType = (type?: string | null): VideoGalleryItemType => {
    const normalized = (type ?? 'youtube').toLowerCase()
    if (allowedTypes.includes(normalized as VideoGalleryItemType)) {
      return normalized as VideoGalleryItemType
    }
    return 'youtube'
  }

  type VideoGalleryItem = NonNullable<DynamicVideoGalleryProps['items']>[number]

  const mediaEmbedItems = (updatedEntry.contentCollection?.items ?? []).filter(
    (item): item is NonNullable<typeof item> => item != null && item.__typename === 'MediaEmbed',
  )

  const items: DynamicVideoGalleryProps['items'] = mediaEmbedItems
    .map((item): VideoGalleryItem | null => {
      if (!item.mediaUrl) return null
      const thumbnailUrl = item.thumbnail?.url ?? undefined
      const thumbnailAlt = item.thumbnail?.description ?? item.thumbnail?.title ?? item.title ?? ''
      const image = thumbnailUrl ? (
        <NextImageWrapper src={thumbnailUrl} alt={thumbnailAlt} sizes={imageSizes} fill />
      ) : undefined

      return {
        id: item.sys.id,
        type: normalizeType(item.type),
        videoUrl: item.mediaUrl,
        title: item.title ?? null,
        image,
        loopVideo: item.loopVideo ?? undefined,
        sectionInspectorProps: {
          // Tag each referenced MediaEmbed entry as one editable section.
          ...buildContentfulInspectorProps(item.sys.id, 'mediaUrl'),
        },
      }
    })
    .filter((item): item is VideoGalleryItem => item != null)

  const gallery = (
    <DynamicVideoGallery
      title={updatedEntry.title}
      view={view}
      items={items}
      theme={theme}
      embedParentDomains={embedParentDomains}
      // Tag the gallery block itself as a page section.
      sectionInspectorProps={inspectorProps('contentCollection')}
    />
  )

  switch (view) {
    case 'slides':
      // Full width for slides
      return gallery
    case 'grid': // Grid is the default view
    default:
      return <Container>{gallery}</Container>
  }
}
